/**
 * 0息游 / 第三方 H5 跳转联登：解析 URL query 中的 phone、type、source 等并调用 shopLogin。
 */

import { shopLogin } from '@/service/index'
import { useUserStore } from '@/store/user'
import { applyEntryLocaleFromPageOptions } from '@/locale/apply-locale'
import { isUnionPayMiniProgramEnv } from '@/utils/base'
import { syncTravelChannelCodeFromPageOptions } from '@/utils/travelChannelCode'

// --- 类型定义 ---

export type ShopJointLoginPageOptions = Record<string, string | undefined> | undefined

export interface ShopJointLoginCallbacks {
  onLoginFailed?: (message: string) => void
}

// --- 常量与变量 ---

const ENTRY_SOURCE_STORAGE_KEY = 'entrySource'

/** travel H5 识别 0息游外跳的 source 固定值（与 wujinbao `TRAVEL_HUB_SOURCE_APP` 对齐） */
export const WUJINBAO_ENTRY_SOURCE = 'wujinbao'

/** travel H5 识别 shopping H5 外跳的 source 固定值 */
export const H5_ENTRY_SOURCE = 'h5'

/** travel H5 识别 uni-app 小程序外跳的 source 固定值 */
export const UNIAPP_ENTRY_SOURCE = 'uniapp'

const KNOWN_ENTRY_SOURCES = new Set([
  WUJINBAO_ENTRY_SOURCE,
  H5_ENTRY_SOURCE,
  UNIAPP_ENTRY_SOURCE,
])

/** `/api/c/order/create`：吉乐游外跳 + 云闪付小程序（非云网） */
export const ORDER_CREATE_ORDER_FROM_JILEYOU_UP_MINI = 1
/** `/api/c/order/create`：吉乐游外跳 + 非云闪付小程序 */
export const ORDER_CREATE_ORDER_FROM_JILEYOU = 5
/** `/api/c/order/create`：出行站内直连（非外跳入站） */
export const ORDER_CREATE_ORDER_FROM_TRAVEL_NATIVE = 6

export interface SyncEntrySourceContext {
  /** 当前是否为 travel 首页（无 source 时需清掉历史 wujinbao 标记） */
  isIndexHome?: boolean
}

// --- 函数 ---

/** 读取外跳入站来源；无值或失败时返回空字符串 */
export function readEntrySourceFromStorage(): string {
  try {
    return String(uni.getStorageSync(ENTRY_SOURCE_STORAGE_KEY) ?? '').trim()
  }
  catch {
    return ''
  }
}

/** 是否从 shopping 电商外跳入站（`source=h5|uniapp`） */
export function isShoppingMallExternalEntry(): boolean {
  const entrySource = readEntrySourceFromStorage()
  return entrySource === H5_ENTRY_SOURCE || entrySource === UNIAPP_ENTRY_SOURCE
}

/** 是否从吉乐游（0息游 `source=wujinbao`）外跳入站 */
export function isJileyouWujinbaoExternalEntry(): boolean {
  return readEntrySourceFromStorage() === WUJINBAO_ENTRY_SOURCE
}

/**
 * 解析 `/api/c/order/create` 的 `orderFrom`：
 * 电商外跳不传；吉乐游外跳云闪付小程序为 1、否则 5；出行站内直连为 6。
 */
export function resolveOrderCreateOrderFromValue(): number | undefined {
  if (isShoppingMallExternalEntry()) {
    return undefined
  }
  if (isJileyouWujinbaoExternalEntry()) {
    return isUnionPayMiniProgramEnv()
      ? ORDER_CREATE_ORDER_FROM_JILEYOU_UP_MINI
      : ORDER_CREATE_ORDER_FROM_JILEYOU
  }
  if (!readEntrySourceFromStorage()) {
    return ORDER_CREATE_ORDER_FROM_TRAVEL_NATIVE
  }
  return undefined
}

/** 按入站来源与宿主环境注入或剥离 `orderFrom`（统一在创单 API 封装层调用） */
export function withTravelOrderCreateOrderFrom<T extends { orderFrom?: number }>(data: T): T {
  const orderFrom = resolveOrderCreateOrderFromValue()
  if (orderFrom === undefined) {
    const { orderFrom: droppedOrderFrom, ...rest } = data
    void droppedOrderFrom
    return rest as T
  }
  return { ...data, orderFrom }
}

/** 清除 0息游外跳来源标记 */
export function clearEntrySource(): void {
  try {
    uni.removeStorageSync(ENTRY_SOURCE_STORAGE_KEY)
  }
  catch {
    // ignore
  }
}

/** URL 是否携带第三方 / 联登类入口 query（不含纯业务参数如 orderNo） */
function hasExternalLandingQuery(options: ShopJointLoginPageOptions): boolean {
  if (!options)
    return false
  if (options.phone !== undefined && normalizeOptionValue(options.phone))
    return true
  if (options.type !== undefined && isJointLoginThirdPartyTypeFlag(options.type))
    return true
  if (options.bankChannel !== undefined && normalizeOptionValue(options.bankChannel))
    return true
  if (options.version !== undefined && normalizeOptionValue(options.version))
    return true
  if (options.showScanPopup !== undefined)
    return true
  if (options.ssoSign !== undefined && normalizeOptionValue(options.ssoSign))
    return true
  if (options.timestamp !== undefined && normalizeOptionValue(options.timestamp))
    return true
  return false
}

/**
 * 按 landing URL 同步 entrySource：source 为 wujinbao / h5 / uniapp 时写入；其它外跳入站或正常首页则清除。
 * 页内跳转（仅 orderNo / id 等业务 query）不改动，避免外跳流程中途丢失标记。
 */
export function syncEntrySourceFromPageOptions(
  options?: ShopJointLoginPageOptions,
  context?: SyncEntrySourceContext,
): void {
  const source = normalizeOptionValue(options?.source)
  if (KNOWN_ENTRY_SOURCES.has(source)) {
    uni.setStorageSync(ENTRY_SOURCE_STORAGE_KEY, source)
    return
  }
  if (source) {
    uni.setStorageSync(ENTRY_SOURCE_STORAGE_KEY, source)
    return
  }
  const isNormalIndexHome = Boolean(context?.isIndexHome)
    && (options?.type === undefined || options.type === false || options.type === 'false')
  if (isNormalIndexHome || hasExternalLandingQuery(options)) {
    clearEntrySource()
  }
}

/** H5 路由 path（含 query）变化时同步 entrySource */
export function syncEntrySourceFromRoutePath(path: string): void {
  const queryIndex = path.indexOf('?')
  const pathname = queryIndex === -1 ? path : path.slice(0, queryIndex)
  const isIndexHome = pathname === '/pages/index/index' || pathname.endsWith('/pages/index/index')
  if (queryIndex === -1) {
    syncEntrySourceFromPageOptions(undefined, { isIndexHome })
    return
  }
  const params = new URLSearchParams(path.slice(queryIndex + 1))
  const options: Record<string, string> = {}
  params.forEach((value, key) => {
    options[key] = value
  })
  syncTravelChannelCodeFromPageOptions(options)
  syncEntrySourceFromPageOptions(options, { isIndexHome })
}

function normalizeOptionValue(raw: string | undefined): string {
  if (raw == null || raw === '')
    return ''
  try {
    return decodeURIComponent(String(raw)).trim()
  }
  catch {
    return String(raw).trim()
  }
}

/** 联登第三方入口 flag（true / false / third），与订单详情等业务页的 orderType 数字参数区分 */
function isJointLoginThirdPartyTypeFlag(raw: unknown): boolean {
  if (raw == null || raw === '')
    return false
  if (raw === false)
    return true
  const normalized = String(raw).trim().toLowerCase()
  return normalized === 'true' || normalized === 'false' || normalized === 'third'
}

/**
 * 页面 onLoad：若携带 phone 则 shopLogin；同步 type / source / bankChannel / language 到 userStore 与 i18n。
 */
export async function applyShopJointLoginFromPageOptions(
  options: ShopJointLoginPageOptions,
  callbacks?: ShopJointLoginCallbacks,
): Promise<void> {
  syncTravelChannelCodeFromPageOptions(options as Record<string, string | undefined>)
  applyEntryLocaleFromPageOptions(options)

  const userStore = useUserStore()

  if (options?.phone != undefined) {
    const mobile = normalizeOptionValue(options.phone)
    if (mobile) {
      const res = await shopLogin({ mobile })
      if (res.code == 200) {
        userStore.setUserInfo(res.data)
        userStore.setToken(res.data.token)
        userStore.setExpire(res.data.expire)
      }
      else {
        callbacks?.onLoginFailed?.(res.msg)
      }
    }
  }

  const hasJointLoginTypeFlag = options?.type !== undefined
    && isJointLoginThirdPartyTypeFlag(options.type)

  if (hasJointLoginTypeFlag) {
    if (options.type == false || options.type == 'false') {
      userStore.setIsThird(false)
    }
    else {
      userStore.setIsThird(true)
    }
    if (options.phone == undefined) {
      userStore.clear()
    }
  }

  if (options?.bankChannel != undefined) {
    userStore.setBankChannel(normalizeOptionValue(options.bankChannel))
  }
  else {
    userStore.setBankChannel('')
  }

  if (options?.activityBankRegion != undefined) {
    const region = normalizeOptionValue(options.activityBankRegion)
    if (region) {
      userStore.setActivityBankRegion(region)
      // 华夏馆入口与广发行 isGF 互斥，清除旅游详情遗留的 GDB 限制
      try {
        uni.removeStorageSync('isGF')
      }
      catch {
        // ignore
      }
    }
  }

  syncEntrySourceFromPageOptions(options)
}
